/**
 * About Us Team – member popup panel and the cursor-following plus button.
 *
 * Deliberately NOT part of the GSAP dependent chain in theme.liquid: neither function needs GSAP
 * (openPanelElement has a CSS-transition fallback, the backdrop is driven by [aria-hidden] in CSS),
 * and the loader there only fires on first interaction or ~1.5-2.5s after load — far too late to
 * open a panel from location.hash on a fresh page load. Loaded with `defer` from the section.
 *
 * @global gsap
 */
(function () {
  const SECTION_SEL = '[data-about-us-team]';
  const CARD_SEL = '[data-aut-card]';
  const PLUS_SEL = '[data-aut-plus]';
  const PANEL_SEL = '[data-aut-panel]';
  const BACKDROP_SEL = '[data-aut-backdrop]';
  const PANEL_CLOSE_SEL = '[data-aut-panel-close]';

  /** Returns true when the viewport is mobile-width (≤750px) */
  function isMobile() {
    return window.innerWidth <= 750;
  }

  /** Plus button follows cursor inside the card image area (desktop hover devices only) */
  function initButtonFollow(section) {
    if (isMobile()) return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    const cards = section.querySelectorAll(CARD_SEL);
    const BTN_HALF = 22;
    const PAD = 8;

    cards.forEach((card) => {
      const imageWrap = card.querySelector('.about-us-team__member-image-wrap');
      const action = card.querySelector('.about-us-team__card-action');
      if (!imageWrap || !action) return;

      card.addEventListener('mousemove', (e) => {
        const rect = imageWrap.getBoundingClientRect();
        let x = e.clientX - rect.left - BTN_HALF;
        let y = e.clientY - rect.top - BTN_HALF;
        x = Math.max(PAD, Math.min(rect.width - BTN_HALF * 2 - PAD, x));
        y = Math.max(PAD, Math.min(rect.height - BTN_HALF * 2 - PAD, y));
        action.style.left = x + 'px';
        action.style.top = y + 'px';
        action.style.right = 'auto';
        action.style.bottom = 'auto';
      });

      card.addEventListener('mouseleave', () => {
        action.style.left = '';
        action.style.top = '';
        action.style.right = '';
        action.style.bottom = '';
      });
    });
  }

  /** @param {HTMLElement} section */
  function initPanel(section) {
    const backdrop = section.querySelector(BACKDROP_SEL);
    const panels = section.querySelectorAll(PANEL_SEL);
    const closeBtns = section.querySelectorAll(PANEL_CLOSE_SEL);

    if (!backdrop || !panels.length) return;

    let activePanel = null;
    let escapeHandler = null;

    function getMemberName(el) {
      return (el.getAttribute('data-aut-member-name') || '').trim();
    }

    function removeEscapeHandler() {
      if (escapeHandler) {
        document.removeEventListener('keydown', escapeHandler);
        escapeHandler = null;
      }
    }

    /**
     * @param {Element} panel
     * @param {boolean} [focusPanel] Move focus inside the dialog — used when the panel was opened
     *   from the URL hash rather than by a click the visitor made.
     */
    function openPanelElement(panel, focusPanel) {
      panels.forEach((p) => p.setAttribute('aria-hidden', 'true'));
      panel.setAttribute('aria-hidden', 'false');
      activePanel = panel;
      backdrop.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (typeof gsap !== 'undefined') {
        gsap.set(panel, { x: '100%' });
        gsap.set(backdrop, { opacity: 0 });
        gsap.to(backdrop, { opacity: 1, duration: 0.4, ease: 'power2.out' });
        gsap.to(panel, { x: 0, duration: 0.6, ease: 'power4.out' });
      } else {
        panel.style.transition = 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
        panel.style.transform = 'translateX(0)';
      }

      escapeHandler = (e) => {
        if (e.key === 'Escape') closePanel();
      };
      document.addEventListener('keydown', escapeHandler);

      if (focusPanel) {
        const closeBtn = panel.querySelector(PANEL_CLOSE_SEL);
        if (closeBtn instanceof HTMLElement) {
          // The dialog is aria-modal and locks body scroll, so focus has to end up inside it.
          // On a hash navigation the browser does its own fragment scroll/focus pass; that
          // finishes by the load event, so wait for it rather than race it from DOMContentLoaded.
          const focusClose = () => requestAnimationFrame(() => closeBtn.focus());
          if (document.readyState === 'complete') {
            focusClose();
          } else {
            window.addEventListener('load', focusClose, { once: true });
          }
        }
      }
    }

    /** @param {Element} card */
    function openPanelFromCard(card) {
      const memberName = getMemberName(card);
      if (!memberName) return;

      const panel = Array.from(panels).find((p) => getMemberName(p) === memberName);
      if (!panel) return;

      openPanelElement(panel);
    }

    function closePanel() {
      if (!activePanel) return;
      removeEscapeHandler();
      const panel = activePanel;
      activePanel = null;

      if (typeof gsap !== 'undefined') {
        gsap.to(panel, {
          x: '100%',
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => {
            panel.setAttribute('aria-hidden', 'true');
            backdrop.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
          }
        });
        gsap.to(backdrop, { opacity: 0, duration: 0.3 });
      } else {
        panel.style.transition = 'transform 0.4s ease-in';
        panel.style.transform = 'translateX(100%)';
        let done = false;
        const finishClose = () => {
          if (done) return;
          done = true;
          panel.removeEventListener('transitionend', onTransitionEnd);
          panel.setAttribute('aria-hidden', 'true');
          backdrop.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        };
        const onTransitionEnd = () => finishClose();
        panel.addEventListener('transitionend', onTransitionEnd);
        setTimeout(finishClose, 450);
      }
    }

    /** /pages/our-team#gentian-shero opens that member's panel on a fresh load */
    function openPanelFromHash() {
      const raw = window.location.hash.slice(1);
      if (!raw) return;
      let id = raw;
      try {
        id = decodeURIComponent(raw);
      } catch (e) {
        // Malformed escape sequence in the hash — fall back to the raw value.
      }
      const panel = Array.from(panels).find((p) => p.id === id);
      if (panel) openPanelElement(panel, true);
    }

    section.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const card = target?.closest(CARD_SEL);
      if (!card) return;
      if (target?.closest(PLUS_SEL)) {
        e.stopPropagation();
        e.preventDefault();
        openPanelFromCard(card);
        return;
      }
      if (window.matchMedia('(hover: none)').matches) {
        e.preventDefault();
        openPanelFromCard(card);
      }
    });

    closeBtns.forEach((btn) => btn.addEventListener('click', closePanel));
    backdrop.addEventListener('click', closePanel);

    openPanelFromHash();
    // Same-document hash changes (back/forward, an on-page anchor) never re-run init.
    window.addEventListener('hashchange', openPanelFromHash);
  }

  /** @param {HTMLElement | null} section */
  function initSection(section) {
    if (!section || section.dataset.autPanelInit === '1') return;
    if (section.dataset.autPopup !== '1') return;
    section.dataset.autPanelInit = '1';

    initPanel(section);
    initButtonFollow(section);
  }

  function init() {
    document.querySelectorAll(SECTION_SEL).forEach((section) => initSection(section));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const section = target.matches(SECTION_SEL) ? target : target.querySelector(SECTION_SEL);
    if (section instanceof HTMLElement) {
      delete section.dataset.autPanelInit;
      initSection(section);
    }
  });
})();
